import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentAppraisals, setRecentAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, appraisalsRes] = await Promise.all([
        axios.get(`${API_URL}/appraisals/dashboard/stats`),
        axios.get(`${API_URL}/appraisals`)
      ]);
      
      setStats(statsRes.data);
      setRecentAppraisals(appraisalsRes.data.slice(0, 10));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAppraisal = async (appraisalId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete the appraisal for ${employeeName}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/appraisals/${appraisalId}`);
      toast.success('Appraisal deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete appraisal');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'DRAFT_SELF': 'outline',
      'SUBMITTED_SELF': 'secondary',
      'IN_MANAGER_REVIEW': 'default',
      'SECOND_LEVEL_REVIEW': 'default',
      'FINALIZED': 'default'
    };
    
    const labels = {
      'DRAFT_SELF': 'Draft',
      'SUBMITTED_SELF': 'Pending Manager',
      'IN_MANAGER_REVIEW': 'Manager Review',
      'SECOND_LEVEL_REVIEW': 'Calibration',
      'FINALIZED': 'Finalized'
    };

    return (
      <Badge variant={variants[status]} className="uppercase tracking-wider text-xs border border-slate-200" data-testid={`status-badge-${status}`}>
        {labels[status]}
      </Badge>
    );
  };

  const getRatingBadge = (rating) => {
    if (!rating) return <span className="text-slate-400">-</span>;
    
    const colors = {
      1: 'text-red-600 bg-red-50 border-red-200',
      2: 'text-orange-600 bg-orange-50 border-orange-200',
      3: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      4: 'text-blue-600 bg-blue-50 border-blue-200',
      5: 'text-green-600 bg-green-50 border-green-200'
    };

    return (
      <span className={`inline-block px-2 py-1 text-xs font-semibold border rounded-sm ${colors[rating]}`} data-testid={`rating-badge-${rating}`}>
        {rating}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-sm text-slate-700">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-sm text-slate-700 mt-2">Overview of your performance appraisals</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="dashboard-stats">
        <Card className="p-4 border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors duration-200">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Visible Appraisals</div>
          <div className="text-2xl font-semibold text-slate-900" data-testid="stat-visible-appraisals">{stats?.visible_appraisals || 0}</div>
        </Card>

        <Card className="p-4 border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors duration-200">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Finalized</div>
          <div className="text-2xl font-semibold text-slate-900" data-testid="stat-finalized">{stats?.finalized || 0}</div>
        </Card>

        <Card className="p-4 border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors duration-200">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">In Progress</div>
          <div className="text-2xl font-semibold text-slate-900" data-testid="stat-in-progress">{stats?.in_progress || 0}</div>
        </Card>

        <Card className="p-4 border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors duration-200">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Avg Finalized Rating</div>
          <div className="text-2xl font-semibold text-slate-900" data-testid="stat-avg-rating">
            {stats?.avg_finalized_rating ? stats.avg_finalized_rating.toFixed(2) : '-'}
          </div>
        </Card>
      </div>

      {/* Recent Appraisals */}
      <Card className="border border-slate-200 bg-white rounded-sm">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-2xl tracking-tight font-semibold text-slate-900" data-testid="recent-appraisals-title">Recent Appraisals</h2>
          <p className="text-sm text-slate-700 mt-1">Latest appraisals you have access to</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-appraisals-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Employee</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cycle</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Self Rating</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Manager Rating</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">2nd-Level Rating</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Updated</th>
                {user?.role === 'HR_ADMIN' && (
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {recentAppraisals.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === 'HR_ADMIN' ? '8' : '7'} className="text-center p-8 text-slate-500">
                    No appraisals found
                  </td>
                </tr>
              ) : (
                recentAppraisals.map((appraisal) => (
                  <tr key={appraisal.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200" data-testid={`appraisal-row-${appraisal.id}`}>
                    <td className="p-4 text-slate-700">{appraisal.employee_name}</td>
                    <td className="p-4 text-slate-700">{appraisal.cycle_name} {appraisal.cycle_year}</td>
                    <td className="p-4">{getStatusBadge(appraisal.status)}</td>
                    <td className="p-4">{getRatingBadge(appraisal.self_overall_rating)}</td>
                    <td className="p-4">{getRatingBadge(appraisal.manager_overall_rating)}</td>
                    <td className="p-4">{getRatingBadge(appraisal.second_level_overall_rating)}</td>
                    <td className="p-4 text-slate-500">
                      {formatDistanceToNow(new Date(appraisal.updated_at), { addSuffix: true })}
                    </td>
                    {user?.role === 'HR_ADMIN' && (
                      <td className="p-4">
                        <Button
                          onClick={() => handleDeleteAppraisal(appraisal.id, appraisal.employee_name)}
                          variant="outline"
                          size="sm"
                          className="border-red-200 text-red-600 hover:bg-red-50 rounded-sm"
                          data-testid={`delete-appraisal-${appraisal.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="p-4 border border-slate-200 bg-white rounded-sm">
        <p className="text-sm text-slate-700 leading-relaxed">
          <strong>Note:</strong> Stats are filtered by the selected user. Employees see their own appraisals, managers see their team's appraisals, and HR admins see all appraisals.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
